import { useContext, useEffect, useState } from 'react';

import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Drawer,
	Flex,
	Form,
	Input,
	Modal,
	Radio,
	Select,
	Tabs,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';

import { useGetLanguages, useSaveQACollectionData } from '@/api/batch_process';
import QAPromptItem from '@/components/common/QAPromptItem';
import { PageContext } from '@/providers/PageContextProvider';

import styles from './style.module.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

type TDateRange = {
	from: string;
	till: string;
};

export default function AddNewQACollectionDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);
	const [form] = useForm();
	const [tabActiveKey, setTabActiveKey] = useState('1');
	const [promptsErrIdxs, setPromptsErrIdxs] = useState<any>([]);

	const { mutate: addQACollection, isLoading } = useSaveQACollectionData();

	const onCancelClick = () => {
		closeDrawer();
		setTabActiveKey('1');
		form.resetFields();
		setPromptsErrIdxs([]);
	};

	const onSaveClick = async () => {
		try {
			setPromptsErrIdxs([]);
			await form.validateFields();

			// const { iab } = form.getFieldsValue();

			// console.log('iab', iab);

			const {
				title,
				description,
				favourite,
				prompts,
				// topics,
			} = form.getFieldsValue();

			addQACollection(
				{
					title: title?.trim(),
					description: description ? description?.trim() : '',
					favourite,

					prompts: prompts?.length
						? prompts?.map((pItem: any) => {
								return {
									title: pItem?.title?.trim(),
									prompt: pItem?.prompt?.trim(),
									trigger: pItem?.trigger || '',
									eval_schema: pItem?.eval_schema || [],
								};
						  })
						: [],
				},
				{
					onSuccess: () => {
						message.success('Added');
						closeDrawer();
						setTabActiveKey('1');
						form.resetFields();
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {
			console.log('error', error);
			message.error('Please fill all required fields');
			if (
				(error as any)?.errorFields?.some((item: any) =>
					item?.name?.includes('prompts'),
				)
			) {
				const promptsErrIdxs =
					[
						...(error as any)?.errorFields
							?.filter((item: any) => item?.name?.includes('prompts'))
							?.map((item: any) => item?.name[1])
							?.filter((item: any) => item !== undefined),
					].reduce((acc: any, curr: any) => {
						if (!acc.includes(curr)) {
							acc.push(curr);
						}
						return acc;
					}, []) || [];

				setPromptsErrIdxs(promptsErrIdxs);
			}
		}
	};

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'add-new-qa-collection'}
			width={'650px'}
			onClose={onCancelClick}
			title="New QA Collection"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					<Button type="primary" onClick={onSaveClick} loading={isLoading}>
						Save
					</Button>
				</Flex>
			</Col>
			<Form
				layout="vertical"
				form={form}
				initialValues={{
					title: '',
					description: '',
					active: false,
					iab: [],
					prompts: [],
				}}
				style={{ marginBottom: '1rem' }}
			>
				<Tabs
					defaultActiveKey="1"
					onChange={(key) => setTabActiveKey(key)}
					activeKey={tabActiveKey}
					style={{ padding: '0 1rem' }}
					items={[
						{
							key: '1',
							label: 'General',
							forceRender: true,
							children: (
								<Flex vertical>
									<Form.Item
										layout="vertical"
										label="Title"
										name="title"
										rules={[
											{ required: true, message: 'Title is required.' },
											{
												whitespace: true,
												message: 'Title cannot be empty.',
											},
										]}
										// initialValue={data?.name}
										// wrapperCol={{ offset: 6, span: 16 }}
										// onMetaChange={}
										// style={{ margin: 0 }}
									>
										<Input />
									</Form.Item>

									<Form.Item
										layout="vertical"
										label="Description"
										name="description"
										// initialValue={data?.description}
										// style={{ marginBottom: "3rem" }}
									>
										<TextArea rows={2} style={{ resize: 'none' }} />
									</Form.Item>

									{/* {data?.active !== undefined && ( */}
									<Form.Item
										layout="vertical"
										// label="Active"
										name="favourite"
										valuePropName="checked"
										// initialValue={data?.active}
										// wrapperCol={{ offset: 6, span: 16 }}
										// onMetaChange={}
										style={{ marginBottom: '1rem' }}
									>
										<Checkbox>favourite</Checkbox>
									</Form.Item>
								</Flex>
							),
						},
						{
							key: '3',
							label: 'Prompts',
							forceRender: true,
							children: (
								<Flex vertical gap={8} align="stretch">
									<Form.List name="prompts">
										{(fields, { add, remove }) => (
											<>
												{fields.map(({ key, name, ...restField }) => (
													<QAPromptItem
														keyProp={key}
														name={name}
														restField={restField}
														deleteDisabled={false}
														hasError={promptsErrIdxs?.includes(name)}
														remove={remove}
													/>
												))}
												<Form.Item
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													<Button
														type="dashed"
														onClick={() => add()}
														disabled={fields.length >= 3}
													>
														Add Prompt
													</Button>
												</Form.Item>
											</>
										)}
									</Form.List>
								</Flex>
							),
						},
					]}
				></Tabs>
			</Form>
		</Drawer>
	);
}
